import { MetaConversation } from 'lib-shared-legacy-types';

import ConversationSummaryHelper from 'app/utils/conversationSummaryHelper';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const REPLY_WINDOW_IN_MILLIS = 24 * HOUR - 1 * MINUTE;

/**
 * Whether the end user has ever opened a reply window on this conversation.
 */
export function hasReplyWindowEverOpened(
  metaConversation: MetaConversation,
): boolean {
  return !!metaConversation.conversation.attributes?.transport_expires_at;
}

/**
 * Returns a timestamp of when an agent can't reply to the conversation any
 * longer.
 */
export function getReplyWindowExpiresOnFromConversation(
  metaConversation: MetaConversation,
): Date | undefined {
  const helper = new ConversationSummaryHelper(metaConversation);

  const { conversation } = metaConversation;

  const transportExpiresAt = conversation.attributes?.transport_expires_at;
  if (transportExpiresAt) {
    return new Date(transportExpiresAt);
  }

  const eventsPresent = (conversation.events?.length ?? 0) > 0;
  const authorsPresent = Object.keys(conversation?.authors ?? {}).length > 0;

  if (eventsPresent && authorsPresent && helper.isChannelWhatsApp()) {
    const endUserHasWritten = metaConversation.messages.some(
      (message) =>
        message.author_id === helper.getRequestedBy() &&
        message.type === 'conversation:message_added',
    );

    if (endUserHasWritten) {
      return undefined;
    }

    return conversation.created_at
      ? new Date(conversation.created_at)
      : new Date(0);
  }

  const channelIsFacebookMessenger = helper.isChannelFacebookMessenger();

  if (!eventsPresent || !authorsPresent || !channelIsFacebookMessenger) {
    return undefined;
  }

  const endUserId = helper.getRequestedBy();

  const lastMessageFromEndUser = [...metaConversation.messages]
    .reverse()
    .find(
      (message) =>
        message.author_id === endUserId &&
        message.type === 'conversation:message_added',
    );

  if (!lastMessageFromEndUser?.created_at) {
    return undefined;
  }

  const lastMessageFromEndUserDate = new Date(
    lastMessageFromEndUser.created_at,
  );

  return new Date(
    lastMessageFromEndUserDate.getTime() + REPLY_WINDOW_IN_MILLIS,
  );
}
